import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface Service {
  id: string;
  tenant_id: string | null;
  name: string;
  name_hi: string | null;
  category: string | null;
  default_price: number;
  cost_price: number;
  is_system_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServiceWithOverride extends Service {
  // Tenant-specific override values
  override_id?: string;
  custom_price?: number | null;
  custom_cost_price?: number | null;
  override_is_active?: boolean;
  // Computed effective values for this tenant
  effective_price: number;
  effective_cost_price: number;
  effective_is_active: boolean;
}

interface TenantServiceOverride {
  id: string;
  tenant_id: string;
  service_id: string;
  custom_price: number | null;
  custom_cost_price: number | null;
  is_active: boolean;
}

export function useServices() {
  const { user, tenant } = useAuth();
  const queryClient = useQueryClient();

  // Fetch services with tenant overrides
  const {
    data: services,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["services", tenant?.id],
    queryFn: async () => {
      // Fetch all applicable services
      const { data: servicesData, error: servicesError } = await supabase
        .from("services")
        .select("*")
        .or(`tenant_id.eq.${tenant?.id},is_system_default.eq.true`)
        .order("category", { ascending: true })
        .order("name", { ascending: true });

      if (servicesError) throw servicesError;

      // Fetch tenant overrides
      const { data: overridesData, error: overridesError } = await supabase
        .from("tenant_service_overrides")
        .select("*")
        .eq("tenant_id", tenant?.id!);

      if (overridesError) throw overridesError;

      const overridesMap = new Map<string, TenantServiceOverride>();
      (overridesData || []).forEach((override: TenantServiceOverride) => {
        overridesMap.set(override.service_id, override);
      });

      // Merge services with overrides
      const servicesWithOverrides: ServiceWithOverride[] = (servicesData || []).map((service: Service) => {
        const override = overridesMap.get(service.id);
        return {
          ...service,
          override_id: override?.id,
          custom_price: override?.custom_price,
          custom_cost_price: override?.custom_cost_price,
          override_is_active: override?.is_active,
          // Effective values: use override if exists, otherwise use default
          effective_price: override?.custom_price ?? service.default_price,
          effective_cost_price: override?.custom_cost_price ?? service.cost_price,
          effective_is_active: override?.is_active ?? service.is_active,
        };
      });

      return servicesWithOverrides;
    },
    enabled: !!user && !!tenant?.id,
  });

  // Create a new tenant-specific service
  const createService = useMutation({
    mutationFn: async (
      service: Omit<Service, "id" | "created_at" | "updated_at" | "is_system_default" | "is_active" | "name_hi"> & { name_hi?: string | null }
    ) => {
      const { data, error } = await supabase
        .from("services")
        .insert({
          ...service,
          name_hi: service.name_hi || null,
          tenant_id: tenant?.id,
          is_system_default: false,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });

  // Update service or create/update override for system defaults
  const updateService = useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: { id: string; default_price?: number; cost_price?: number; name?: string; category?: string | null }) => {
      // Find the service
      const service = services?.find(s => s.id === id);
      if (!service) throw new Error("Service not found");

      if (service.is_system_default) {
        // For system defaults, create or update an override
        const overrideData = {
          tenant_id: tenant?.id!,
          service_id: id,
          custom_price: updates.default_price ?? null,
          custom_cost_price: updates.cost_price ?? null,
          is_active: true,
        };

        if (service.override_id) {
          // Update existing override
          const { data, error } = await supabase
            .from("tenant_service_overrides")
            .update({
              custom_price: updates.default_price ?? null,
              custom_cost_price: updates.cost_price ?? null,
            })
            .eq("id", service.override_id)
            .select()
            .single();

          if (error) throw error;
          return data;
        } else {
          // Create new override
          const { data, error } = await supabase
            .from("tenant_service_overrides")
            .insert(overrideData)
            .select()
            .single();

          if (error) throw error;
          return data;
        }
      } else {
        // For tenant's own services, update directly
        const { data, error } = await supabase
          .from("services")
          .update(updates)
          .eq("id", id)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });

  // Toggle service active status
  const toggleServiceActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const service = services?.find(s => s.id === id);
      if (!service) throw new Error("Service not found");

      if (service.is_system_default) {
        // For system defaults, use override
        if (service.override_id) {
          const { data, error } = await supabase
            .from("tenant_service_overrides")
            .update({ is_active })
            .eq("id", service.override_id)
            .select()
            .single();

          if (error) throw error;
          return data;
        } else {
          // Create new override with active status
          const { data, error } = await supabase
            .from("tenant_service_overrides")
            .insert({
              tenant_id: tenant?.id!,
              service_id: id,
              is_active,
            })
            .select()
            .single();

          if (error) throw error;
          return data;
        }
      } else {
        // For tenant's own services, update directly
        const { data, error } = await supabase
          .from("services")
          .update({ is_active })
          .eq("id", id)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });

  // Delete service (only for tenant's own services)
  const deleteService = useMutation({
    mutationFn: async (id: string) => {
      const service = services?.find(s => s.id === id);
      if (!service) throw new Error("Service not found");

      if (service.is_system_default) {
        throw new Error("Cannot delete system default services. You can disable them instead.");
      }

      // Try hard delete first
      const { error } = await supabase
        .from("services")
        .delete()
        .eq("id", id);
      
      if (error) {
        // Check if it's a foreign key constraint error
        if (error.code === '23503' || error.message.includes('foreign key constraint')) {
          throw new Error('This service has been used in sales and cannot be deleted. You can disable it instead to hide from billing.');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });

  // Reset service to default (remove override)
  const resetToDefault = useMutation({
    mutationFn: async (serviceId: string) => {
      const service = services?.find(s => s.id === serviceId);
      if (!service?.override_id) return;

      const { error } = await supabase
        .from("tenant_service_overrides")
        .delete()
        .eq("id", service.override_id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });

  // Get only active services for billing (using effective values)
  const activeServices = services?.filter(s => s.effective_is_active) || [];

  return {
    services,
    activeServices,
    isLoading,
    error,
    refetch,
    createService,
    updateService,
    deleteService,
    toggleServiceActive,
    resetToDefault,
  };
}
